/*
  Use guide / 使いい方
    1. Container with classname favorite-buttons-container
    2. Child class with that could selected by button.favorite-button
    3. Multiple container element allowed.

  Example elements to use this　/ 使うために作らなきゃいけないエレメント例
    <div class="any-name favorite-buttons-container">
      <div class="any-item-name favorite-button"></div>
      <div class="any-item-name favorite-button"></div>
      <div class="any-item-name favorite-button"></div>
      ...
    </div>
    or
    <div class="any-name favorite-buttons-container">
      <div class="any-item-name"></div> <- no error but will not functioning
      <div class="any-item-name">
        <div class="any-name favorite-button"></div>  <- valid button
      </div>
      <div class="any-item-name favorite-button"></div>
      ...
    </div>

    2025/June/05 This class used by
      - snippets/products-recommendation.liquid (favorite-button here)-> snippets/product-grid-item.liquid
      - coordinate.liquid (favorite-button here)-> snippets/coordinate-content.liquid
      - product.liquid
      - coordinate-list.liquid
      - ranking.liquid
*/

const dispatchHapticEvent = (kind) => {
  if (typeof window === 'undefined') return;
  const send = window.sendHapticToNative;
  if (typeof send === 'function') {
    send(kind);
  }
};

const notifyFavoriteAdded = () => dispatchHapticEvent('favorite_add');
const notifyFavoriteError = () => dispatchHapticEvent('error');

class FavoriteButton {
  constructor() {
    this.initialStates = new Map();
    this.buttons = new Set();
    this.handleDocumentClick = this.handleDocumentClick.bind(this);

    this.registerExistingButtons();
    this.observeNewButtons();
    document.addEventListener('click', this.handleDocumentClick);
    this.handlePendingFavorite();
  }

  registerExistingButtons() {
    document
      .querySelectorAll('[data-favorite-button]')
      .forEach((button) => this.registerButton(button));
  }

  registerButton(button) {
    if (!(button instanceof HTMLElement)) return;
    if (this.buttons.has(button)) return;
    this.buttons.add(button);
    const id = this.getButtonId(button);
    if (id) {
      this.initialStates.set(id, button.classList.contains('is-active'));
    }
  }

  getButtonId(button) {
    if (!button || !(button instanceof HTMLElement)) return null;
    const type = button.dataset.type;
    if (type === 'item') return button.dataset.productId || null;
    if (type === 'coordinate') return button.dataset.coordinateId || null;
    return null;
  }

  observeNewButtons() {
    if (typeof MutationObserver === 'undefined') return;
    this.buttonObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches && node.matches('[data-favorite-button]')) {
            this.registerButton(node);
          }
          if (node.querySelectorAll) {
            node
              .querySelectorAll('[data-favorite-button]')
              .forEach((child) => this.registerButton(child));
          }
        });
      });
    });

    this.buttonObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  handleDocumentClick(event) {
    const button = event.target.closest('button.favorite-button[data-favorite-button]');
    if (!button) return;
    event.preventDefault();
    this.handleButtonClick(button);
  }

  updateButtonStates() {
    this.buttons.forEach((button) => {
      const customerId = button.dataset.customerId;
      if (!customerId) return;

      const type = button.dataset.type;
      if (type === 'coordinate') {
        const coordinateId = button.dataset.coordinateId;
        if (window.favoriteStates && window.favoriteStates[coordinateId]) {
          button.classList.add('is-active');
        } else if (window.favoriteStates) {
          button.classList.remove('is-active');
        }
      } else if (type === 'item') {
        const itemId = button.dataset.productId;
        if (window.favoriteStates && window.favoriteStates[itemId]) {
          button.classList.add('is-active');
        } else if (window.favoriteStates) {
          button.classList.remove('is-active');
        }
      }
    });
  }

  async waitForFavoriteStates() {
    let attempts = 0;
    const maxAttempts = 50;

    while (attempts < maxAttempts) {
      if (window.favoriteStates) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts++;
    }
    return;
  }

  async handleButtonClick(button) {
    const customerId = button.dataset.customerId;
    const type = button.dataset.type;
    const isCurrentlyFavorited = button.classList.contains('is-active');

    if (!customerId) {
      const productId = button.dataset.productId;
      const coordinateId = button.dataset.coordinateId;
      const id = type === 'item' ? productId : coordinateId;

      const pendingData = {
        action: isCurrentlyFavorited ? 'remove' : 'add',
        [type === 'item' ? 'itemId' : 'coordinateId']: id,
      };
      localStorage.setItem('pendingFavorite', JSON.stringify(pendingData));

      this.showLoginRequiredMessage(button, id);
      return;
    }

    try {
      if (type === 'coordinate') {
        const coordinateId = button.dataset.coordinateId;
        const action = isCurrentlyFavorited ? 'remove' : 'add';

        const response = await fetch(
          'https://af.n203.jp/api/operate-favorite-coordinate',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              action,
              coordinateId,
              customerId,
            }),
          }
        );

        const result = await response.json();

        if (result === 'success') {
          if (action === 'add') {
            button.classList.add('is-active');
            if (window.favoriteStates) {
              window.favoriteStates[coordinateId] = true;
            }
            this.updateLikedCount(button, 1);
            notifyFavoriteAdded();
          } else {
            button.classList.remove('is-active');
            if (window.favoriteStates) {
              window.favoriteStates[coordinateId] = false;
            }
            this.updateLikedCount(button, -1);
          }
        }
      } else if (type === 'item') {
        const action = isCurrentlyFavorited ? 'remove' : 'add';
        const itemId = button.dataset.productId;

        const response = await fetch(
          'https://af.n203.jp/api/operate-favorite-item',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              action,
              customerId,
              itemId,
            }),
          }
        );

        const result = await response.json();

        if (result === 'success') {
          if (action === 'add') {
            button.classList.add('is-active');
            if (window.favoriteStates) {
              window.favoriteStates[itemId] = true;
            }
            this.updateLikedCount(button, 1);
            notifyFavoriteAdded();
          } else {
            button.classList.remove('is-active');
            if (window.favoriteStates) {
              window.favoriteStates[itemId] = false;
            }
            this.updateLikedCount(button, -1);
          }
        }
      }
    } catch (error) {
      console.error('Error updating favorite status:', error);
      notifyFavoriteError();
    }
  }

  updateLikedCount(button, delta) {
    const countLikedEl = button.querySelector('.count-liked');
    if (!countLikedEl) return;
    const currentValue = Number(countLikedEl.textContent) || 0;
    const nextValue = Math.max(0, currentValue + delta);
    countLikedEl.textContent = nextValue > 0 ? String(nextValue) : '';
  }

  showLoginRequiredMessage(button, id) {
    const existingPopup = document.querySelector('.login-required-toast');
    if (existingPopup) {
      existingPopup.remove();
    }

    const type = button.dataset.type;

    const popup = document.createElement('div');
    popup.className = 'login-required-toast';
    popup.innerHTML = `
      <div class="toast-content">
        <p>保存にはログインが必要です。</p>
        <div class="toast-buttons">
          <button class="btn btn-primary login-button">ログインページはこちら。</button>
        </div>
      </div>
    `;

    document.body.appendChild(popup);

    const loginButton = popup.querySelector('.login-button');
    loginButton.addEventListener('click', () => {
      const pendingData = {
        action: 'add',
        customerId: '',
        [type === 'item' ? 'itemId' : 'coordinateId']: id,
      };
      localStorage.setItem('pendingFavorite', JSON.stringify(pendingData));

      const currentPath = window.location.pathname;
      const encodedCurrentPath = encodeURIComponent(currentPath);
      window.location.href = `/account/login?return_url=${encodedCurrentPath}`;
    });

    setTimeout(() => popup.remove(), 3000);
  }

  async handlePendingFavorite() {
    const pendingData = localStorage.getItem('pendingFavorite');
    if (!pendingData) return;

    try {
      const data = JSON.parse(pendingData);
      const customerId = document.querySelector('[data-favorite-button]')
        ?.dataset.customerId;

      if (!customerId) return;

      const isCoordinate = 'coordinateId' in data;
      const endpoint = isCoordinate
        ? 'https://af.n203.jp/api/operate-favorite-coordinate'
        : 'https://af.n203.jp/api/operate-favorite-item';

      const requestBody = {
        action: data.action,
        customerId: customerId,
        [isCoordinate ? 'coordinateId' : 'itemId']: isCoordinate
          ? data.coordinateId
          : data.itemId,
      };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (result === 'success') {
        const selector = isCoordinate
          ? `[data-coordinate-id="${data.coordinateId}"]`
          : `[data-product-id="${data.itemId}"]`;

        const button = document.querySelector(selector);
        if (button) {
          button.classList.add('is-active');

          if (window.favoriteStates) {
            window.favoriteStates[
              isCoordinate ? data.coordinateId : data.itemId
            ] = true;
          }
        }

        localStorage.removeItem('pendingFavorite');

        this.showSaveCompletedMessage(isCoordinate);
      }
    } catch (error) {
      console.error('Error handling pending favorite:', error);
    }
  }

  showSaveCompletedMessage(isCoordinate) {
    const existingPopup = document.querySelector('.save-completed-toast');
    if (existingPopup) {
      existingPopup.remove();
    }

    const popup = document.createElement('div');
    popup.className = 'save-completed-toast';
    popup.innerHTML = `
      <div class="toast-content">
        <p>${isCoordinate ? 'コーデ' : 'アイテム'}の保存が完了しました！</p>
        <div class="toast-buttons">
          <button class="btn btn-primary view-button">
            ${isCoordinate ? 'コーデ' : 'アイテム'}を見に行く
          </button>
        </div>
      </div>
    `;

    const style = document.createElement('style');
    style.textContent = `
      .save-completed-toast {
        position: fixed;
        bottom: 20px;
        left: 20px;
        background: white;
        padding: 15px 20px;
        border-radius: 4px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        z-index: 1000;
        animation: slideIn 0.3s ease-out, fadeOut 0.3s ease-out 2.7s;
        max-width: 300px;
      }
      
      .toast-content {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      
      .toast-content p {
        margin: 0;
        font-size: 14px;
      }
      
      .toast-buttons {
        display: flex;
        gap: 10px;
      }
      
      .toast-buttons .btn {
        font-size: 13px;
        padding: 5px 15px;
        cursor: pointer;
      }
    `;

    document.head.appendChild(style);
    document.body.appendChild(popup);

    const viewButton = popup.querySelector('.view-button');
    viewButton.addEventListener('click', () => {
      window.location.href = isCoordinate
        ? '/pages/favorite-coordinates'
        : '/pages/favorite-items';
    });

    setTimeout(() => {
      popup.remove();
    }, 5000);
  }
}

window.addEventListener('load', () => {
  new FavoriteButton();
});
